class CommunityBuilds extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.community-builds__grid');
    this.cards = [...this.querySelectorAll('[data-community-build-card]')];
    this.loadMoreButton = this.querySelector('[data-community-builds-load-more]');
    this.status = this.querySelector('[data-community-builds-status]');
    this.modal = this.querySelector('[data-community-build-modal]');
    this.modalBody = this.querySelector('[data-community-build-modal-body]');
    this.modalPosition = this.querySelector('[data-community-build-modal-position]');
    this.previousButton = this.querySelector('[data-community-build-previous]');
    this.nextButton = this.querySelector('[data-community-build-next]');
    this.filtersContainer = this.querySelector('.community-builds-section__filters');
    this.filterButtons = [];
    this.nextPageUrl = this.dataset.nextPageUrl || '';
    this.loadingLabel =
      this.querySelector('[data-community-build-modal-loading]')?.textContent.trim() ||
      'Loading build…';
    this.detailCache = new Map();
    this.loadedBuildKeys = new Set(this.cards.map((card) => this.cardKey(card)).filter(Boolean));
    this.boundCards = new WeakSet();
    this.boundFilterButtons = new WeakSet();
    this.paginationPromise = null;
    this.paginationFailed = false;
    this.paginationSchedule = null;
    this.requestId = 0;
    this.abortController = new AbortController();

    if (this.dataset.randomize === 'true') {
      this.shuffleCards();
    }

    this.loadMoreButton?.addEventListener('click', () => this.showNextBatch(), {
      signal: this.abortController.signal,
    });

    this.cards.forEach((card) => this.bindCard(card));

    this.previousButton?.addEventListener('click', () => this.navigateBuild(-1), {
      signal: this.abortController.signal,
    });
    this.nextButton?.addEventListener('click', () => this.navigateBuild(1), {
      signal: this.abortController.signal,
    });
    this.modal?.addEventListener(
      'on:popup:closed',
      () => {
        this.currentCard = null;
        this.requestId += 1;
      },
      { signal: this.abortController.signal },
    );
    document.addEventListener('keydown', (event) => this.handleModalKeydown(event), {
      signal: this.abortController.signal,
    });

    if (this.dataset.enableFilters === 'true') {
      this.filterButtons = [...this.querySelectorAll('[data-community-builds-filter]')];
      this.updateFilterCounts();
      this.filterButtons.forEach((button) => this.bindFilterButton(button));

      window.addEventListener(
        'popstate',
        () => this.applyFilter(this.modelFromUrl(), false),
        { signal: this.abortController.signal },
      );

      this.applyFilter(this.modelFromUrl(), false);
    } else if (this.dataset.randomize === 'true') {
      this.currentModel = this.normalizeModel(this.dataset.activeModel);
      this.showNextBatch();
    } else {
      this.currentModel = this.normalizeModel(this.dataset.activeModel);
      this.updateControls();
    }

    this.scheduleRemainingPages();
  }

  disconnectedCallback() {
    this.cancelScheduledPagination();
    this.abortController?.abort();
    this.requestId += 1;
  }

  cardKey(card) {
    return (
      card.dataset.communityBuildId ||
      card.querySelector('[data-community-build-open]')?.href ||
      ''
    );
  }

  bindCard(card) {
    if (this.boundCards.has(card)) return;

    this.boundCards.add(card);
    card.querySelector('[data-community-build-open]')?.addEventListener(
      'click',
      (event) => this.handleCardClick(event, card),
      { signal: this.abortController.signal },
    );
  }

  bindFilterButton(button) {
    if (this.boundFilterButtons.has(button)) return;

    this.boundFilterButtons.add(button);
    button.addEventListener(
      'click',
      () => this.applyFilter(button.dataset.communityBuildsFilter, true),
      { signal: this.abortController.signal },
    );
  }

  scheduleRemainingPages() {
    if (!this.nextPageUrl) return;

    const loadPages = () => {
      this.paginationSchedule = null;
      this.startPagination();
    };

    if ('requestIdleCallback' in window) {
      this.paginationSchedule = {
        type: 'idle',
        id: window.requestIdleCallback(loadPages, { timeout: 1500 }),
      };
    } else {
      this.paginationSchedule = {
        type: 'timeout',
        id: window.setTimeout(loadPages, 500),
      };
    }
  }

  cancelScheduledPagination() {
    if (!this.paginationSchedule) return;

    if (this.paginationSchedule.type === 'idle') {
      window.cancelIdleCallback(this.paginationSchedule.id);
    } else {
      window.clearTimeout(this.paginationSchedule.id);
    }

    this.paginationSchedule = null;
  }

  startPagination() {
    if (!this.nextPageUrl) return this.paginationPromise || Promise.resolve();
    if (this.paginationPromise) return this.paginationPromise;

    this.cancelScheduledPagination();
    this.paginationFailed = false;
    this.setPaginationLoading(true);

    this.paginationPromise = this.loadRemainingPages()
      .then(() => this.refreshAfterPagination())
      .catch((error) => {
        if (error.name === 'AbortError') return;

        this.paginationFailed = true;
        console.error(error);
      })
      .finally(() => {
        this.paginationPromise = null;
        this.setPaginationLoading(false);
        this.updateControls();
      });

    return this.paginationPromise;
  }

  async loadRemainingPages() {
    const visitedUrls = new Set();

    while (this.nextPageUrl) {
      const pageUrl = new URL(this.nextPageUrl, window.location.origin).toString();
      if (visitedUrls.has(pageUrl)) {
        throw new Error('Community build pagination returned the same page twice.');
      }

      visitedUrls.add(pageUrl);
      const page = await this.fetchBuildPage(pageUrl);
      this.mergeBuildPage(page);
    }
  }

  async fetchBuildPage(url) {
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(sectionUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Community build page request failed: ${response.status}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const page = [...documentFragment.querySelectorAll('community-builds')].find(
      (element) => element.dataset.sectionId === this.dataset.sectionId,
    );

    if (!page) throw new Error('Community build page was not found in the section response.');
    return page;
  }

  mergeBuildPage(page) {
    const newCards = document.createDocumentFragment();

    page.querySelectorAll('[data-community-build-card]').forEach((sourceCard) => {
      const buildKey = this.cardKey(sourceCard);
      if (!buildKey || this.loadedBuildKeys.has(buildKey)) return;

      const card = document.importNode(sourceCard, true);
      card.classList.add('community-build-card--deferred');
      this.loadedBuildKeys.add(buildKey);
      this.cards.push(card);
      this.bindCard(card);
      newCards.append(card);
    });

    this.grid?.append(newCards);
    this.mergeFilterButtons(page);
    this.nextPageUrl = page.dataset.nextPageUrl || '';
    this.dataset.nextPageUrl = this.nextPageUrl;
    this.dataset.totalBuilds = String(this.cards.length);
  }

  mergeFilterButtons(page) {
    if (this.dataset.enableFilters !== 'true') return;

    const sourceContainer = page.querySelector('.community-builds-section__filters');
    if (!sourceContainer) return;

    if (!this.filtersContainer) {
      this.filtersContainer = document.importNode(sourceContainer, true);
      this.querySelector('.community-builds')?.before(this.filtersContainer);
      this.filterButtons = [
        ...this.filtersContainer.querySelectorAll('[data-community-builds-filter]'),
      ];
      this.filterButtons.forEach((button) => this.bindFilterButton(button));
      return;
    }

    sourceContainer.querySelectorAll('[data-community-builds-filter]').forEach((sourceButton) => {
      const model = this.normalizeModel(sourceButton.dataset.communityBuildsFilter);
      const exists = this.filterButtons.some(
        (button) => this.normalizeModel(button.dataset.communityBuildsFilter) === model,
      );
      if (exists) return;

      const button = document.importNode(sourceButton, true);
      this.filtersContainer.append(button);
      this.filterButtons.push(button);
      this.bindFilterButton(button);
    });
  }

  refreshAfterPagination() {
    const shownBuilds = this.matchingCards().filter(
      (card) => !card.classList.contains('community-build-card--deferred'),
    ).length;

    if (this.dataset.randomize === 'true') {
      this.shuffleCards();
    } else {
      this.sortCardsByDate();
    }

    if (
      this.currentModel !== 'all' &&
      !this.cards.some(
        (card) =>
          this.normalizeModel(card.dataset.communityBuildModel) === this.currentModel,
      )
    ) {
      this.currentModel = 'all';
    }

    this.cards.forEach((card) => card.classList.add('community-build-card--deferred'));
    const matchingCards = this.matchingCards();
    const revealCount = Math.max(
      shownBuilds,
      Math.min(this.currentBatchSize(), matchingCards.length),
    );
    matchingCards.slice(0, revealCount).forEach((card) => this.revealCard(card));

    this.filterButtons.forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(this.normalizeModel(button.dataset.communityBuildsFilter) === this.currentModel),
      );
    });

    if (matchingCards.length > 0) this.hidden = false;
    this.updateFilterCounts();
    this.updateControls();
    this.updateModalNavigation();
  }

  sortCardsByDate() {
    this.cards.sort((firstCard, secondCard) => {
      const dateComparison = (secondCard.dataset.communityBuildDate || '').localeCompare(
        firstCard.dataset.communityBuildDate || '',
      );
      if (dateComparison !== 0) return dateComparison;

      return this.cardKey(secondCard).localeCompare(this.cardKey(firstCard), undefined, {
        numeric: true,
      });
    });

    this.cards.forEach((card) => this.grid?.append(card));
  }

  setPaginationLoading(isLoading) {
    if (!this.loadMoreButton) return;

    if (isLoading) {
      this.loadMoreButton.setAttribute('aria-busy', 'true');
    } else {
      this.loadMoreButton.removeAttribute('aria-busy');
    }
  }

  handleCardClick(event, card) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !this.modal
    ) {
      return;
    }

    event.preventDefault();
    this.openBuild(card);
  }

  async openBuild(card) {
    this.currentCard = card;
    this.updateModalNavigation();
    this.showModalLoading();
    await this.modal.open();
    this.loadBuild(card);
  }

  async loadBuild(card) {
    const link = card.querySelector('[data-community-build-open]');
    if (!link || !this.modalBody) return;

    const requestId = ++this.requestId;
    this.showModalLoading();

    try {
      const html = await this.fetchBuildDetail(link.href);
      if (requestId !== this.requestId || card !== this.currentCard) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const detail = documentFragment.querySelector('[data-community-build-detail]');
      if (!detail) throw new Error('Community build detail was not found in the response.');

      this.modalBody.replaceChildren(detail);
      this.modalBody.removeAttribute('aria-busy');
      this.modal.querySelector('.popup__content')?.scrollTo({ top: 0, behavior: 'auto' });
    } catch (error) {
      if (requestId !== this.requestId || card !== this.currentCard) return;

      const message = document.createElement('p');
      message.className = 'community-build-modal__error';
      message.textContent = 'This build could not be loaded.';

      const fallbackLink = link.cloneNode();
      fallbackLink.removeAttribute('data-community-build-open');
      fallbackLink.className = 'c-btn c-btn--hollow';
      fallbackLink.textContent = 'View build page';

      this.modalBody.replaceChildren(message, fallbackLink);
      this.modalBody.removeAttribute('aria-busy');
      console.error(error);
    }
  }

  fetchBuildDetail(url) {
    const ajaxUrl = new URL(url, window.location.origin);
    ajaxUrl.searchParams.set('view', 'popup');
    const cacheKey = ajaxUrl.toString();

    if (!this.detailCache.has(cacheKey)) {
      const request = fetch(cacheKey, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Community build request failed: ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          this.detailCache.delete(cacheKey);
          throw error;
        });

      this.detailCache.set(cacheKey, request);
    }

    return this.detailCache.get(cacheKey);
  }

  showModalLoading() {
    if (!this.modalBody) return;

    const loading = document.createElement('div');
    loading.className = 'community-build-modal__loading';
    loading.textContent = this.loadingLabel;
    this.modalBody.setAttribute('aria-busy', 'true');
    this.modalBody.replaceChildren(loading);
  }

  navigateBuild(direction) {
    const cards = this.matchingCards();
    const currentIndex = cards.indexOf(this.currentCard);
    if (cards.length < 2 || currentIndex < 0) return;

    const nextIndex = (currentIndex + direction + cards.length) % cards.length;
    this.currentCard = cards[nextIndex];
    this.updateModalNavigation();
    this.loadBuild(this.currentCard);
  }

  handleModalKeydown(event) {
    if (!this.modal?.shown || !this.currentCard) return;
    if (event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
    if (event.target.closest('[data-community-build-gallery]')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateBuild(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateBuild(1);
    }
  }

  updateModalNavigation() {
    const cards = this.matchingCards();
    const currentIndex = cards.indexOf(this.currentCard);
    const canNavigate = cards.length > 1 && currentIndex >= 0;

    if (this.modalPosition) {
      const position = currentIndex >= 0 ? currentIndex + 1 : 0;
      this.modalPosition.textContent = `${this.padNumber(position)} / ${this.padNumber(cards.length)}`;
    }

    [this.previousButton, this.nextButton].forEach((button) => {
      if (!button) return;
      button.hidden = !canNavigate;
      button.disabled = !canNavigate;
    });

    if (!canNavigate) return;

    const previousCard = cards[(currentIndex - 1 + cards.length) % cards.length];
    const nextCard = cards[(currentIndex + 1) % cards.length];
    this.setNavigationLabel(this.previousButton, previousCard);
    this.setNavigationLabel(this.nextButton, nextCard);
  }

  setNavigationLabel(button, card) {
    if (!button || !card) return;
    const visibleLabel = button.querySelector(':scope > span:not(.icon)')?.textContent.trim() || '';
    const buildTitle = card.dataset.communityBuildTitle || '';
    button.setAttribute('aria-label', `${visibleLabel}: ${buildTitle}`);
  }

  padNumber(number) {
    return number < 10 ? `0${number}` : String(number);
  }

  async showNextBatch() {
    let hiddenCards = this.matchingCards().filter((card) =>
      card.classList.contains('community-build-card--deferred'),
    );
    const shownBefore = this.matchingCards().length - hiddenCards.length;

    if (hiddenCards.length === 0 && (this.nextPageUrl || this.paginationPromise)) {
      await this.startPagination();
      hiddenCards = this.matchingCards().filter((card) =>
        card.classList.contains('community-build-card--deferred'),
      );

      const shownAfter = this.matchingCards().length - hiddenCards.length;
      if (shownAfter > shownBefore) return;
    }

    hiddenCards.slice(0, this.currentBatchSize()).forEach((card) => this.revealCard(card));
    this.updateControls();
  }

  applyFilter(model, updateUrl) {
    const availableModels = new Set(
      this.filterButtons.map((button) => this.normalizeModel(button.dataset.communityBuildsFilter)),
    );
    const normalizedModel = this.normalizeModel(model);
    const modelMayLoad = normalizedModel !== 'all' && Boolean(this.nextPageUrl || this.paginationPromise);
    this.currentModel =
      availableModels.has(normalizedModel) || modelMayLoad ? normalizedModel : 'all';

    this.cards.forEach((card) => card.classList.add('community-build-card--deferred'));
    this.filterButtons.forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(this.normalizeModel(button.dataset.communityBuildsFilter) === this.currentModel),
      );
    });

    this.showNextBatch();

    if (updateUrl) {
      const url = new URL(window.location.href);

      if (this.currentModel === 'all') {
        url.searchParams.delete('model');
      } else {
        url.searchParams.set('model', this.currentModel);
      }

      window.history.pushState({}, '', url);
    }
  }

  matchingCards() {
    if (!this.currentModel || this.currentModel === 'all') return this.cards;

    return this.cards.filter(
      (card) => this.normalizeModel(card.dataset.communityBuildModel) === this.currentModel,
    );
  }

  revealCard(card) {
    const imageTemplate = card.querySelector('[data-community-build-image]');

    if (imageTemplate) {
      imageTemplate.replaceWith(imageTemplate.content.cloneNode(true));
    }

    card.classList.remove('community-build-card--deferred');
  }

  updateControls() {
    const matchingCards = this.matchingCards();
    const shownBuilds = matchingCards.filter(
      (card) => !card.classList.contains('community-build-card--deferred'),
    ).length;
    const totalBuilds = matchingCards.length;

    if (this.status) {
      this.status.textContent = this.paginationFailed
        ? 'More builds could not be loaded. Try again.'
        : `Showing ${shownBuilds} of ${totalBuilds} ${totalBuilds === 1 ? 'build' : 'builds'}`;
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.hidden =
        shownBuilds >= totalBuilds && !this.nextPageUrl && !this.paginationPromise;
    }
  }

  updateFilterCounts() {
    this.filterButtons.forEach((button) => {
      const model = this.normalizeModel(button.dataset.communityBuildsFilter);
      const count =
        model === 'all'
          ? this.cards.length
          : this.cards.filter(
              (card) => this.normalizeModel(card.dataset.communityBuildModel) === model,
            ).length;
      const countElement = button.querySelector('[data-community-builds-filter-count]');

      if (countElement) {
        countElement.textContent = ` (${count})`;
      }
    });
  }

  currentBatchSize() {
    const mobileBatchSize = Number.parseInt(this.dataset.batchSize, 10) || 12;
    const desktopBatchSize =
      Number.parseInt(this.dataset.desktopBatchSize, 10) || mobileBatchSize;

    return window.matchMedia('(min-width: 768px)').matches
      ? desktopBatchSize
      : mobileBatchSize;
  }

  modelFromUrl() {
    return new URL(window.location.href).searchParams.get('model') || 'all';
  }

  normalizeModel(model = '') {
    const normalizedModel = model
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const aliases = {
      'jibb-v1': 'jibb',
      'jibb-v2-2': 'jibb-v2',
      'jibb-lt-v2': 'jibb-v2',
      'jibb-lt-v2-2': 'jibb-v2',
      'yalla-v1': 'yalla',
      'yalla-v2': 'yalla',
    };

    return aliases[normalizedModel] || normalizedModel || 'all';
  }

  shuffleCards() {
    for (let index = this.cards.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [this.cards[index], this.cards[randomIndex]] = [this.cards[randomIndex], this.cards[index]];
    }

    this.cards.forEach((card) => this.grid?.append(card));
  }
}

if (!customElements.get('community-builds')) {
  customElements.define('community-builds', CommunityBuilds);
}
